import { useCallback, useState } from 'react';
import { ActiveTradesPanelResponse, useGetActiveTradesPanelQuery } from 'store/services/trade/tradeApi';
import { useTradePanelCollapsed } from './useTradePanelCollapsed';

export interface TradePanelState {
  data?: ActiveTradesPanelResponse;
  isLoading: boolean;
  /**
   * The panel cannot be shown: its request failed with nothing cached (a backend
   * that predates the endpoint 404s it) or its chunk failed to load or render.
   * Both degrade to the collapsed layout, with no Expand control to offer.
   */
  broken: boolean;
  /** The user's choice (or the breakpoint default), forced true while broken */
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
  onRenderFailed: () => void;
  /** The search term this state was fetched with, for the map's context layer */
  search: string;
}

/**
 * Everything /sending and /receiving need to drive TradePanelSection.
 * `search` must be the SAME debounced value the list query uses, so the list,
 * the badges and the panel can never disagree mid-typing.
 */
export const useTradePanelState = (
  type: 'sending' | 'receiving',
  search: string,
  skip: boolean
): TradePanelState => {
  const [collapsed, setCollapsed] = useTradePanelCollapsed();
  const [renderFailed, setRenderFailed] = useState(false);
  const { data, isLoading, isError } = useGetActiveTradesPanelQuery(
    { type, search: search.trim() || undefined },
    { skip }
  );
  const onRenderFailed = useCallback(() => setRenderFailed(true), []);

  const broken = renderFailed || (isError && !data);
  return { data, isLoading, broken, collapsed: collapsed || broken, setCollapsed, onRenderFailed, search };
};
